import React, { useEffect, useState, useCallback } from "react";
import {
  fetchUserManagementPermissionGroupsForApp,
  fetchUserManagementPermissionGroup,
  fetchUserManagementPermissionGroupForApp,
  fetchUserManagementPermissions,
  fetchUserManagementPermissionsForApp,
  createUserManagementPermissionGroup,
  createUserManagementPermissionGroupForApp,
  updateUserManagementPermissionGroup,
  updateUserManagementPermissionGroupForApp,
} from "@shared/api/userManagement";
import useListPageUrlState from "../../hooks/useListPageUrlState";
import { ListPageLayout, SortableTh, CtaButton } from "../../components/admin/ListPageLayout";
import Select from "@shared/components/Select";
import { buildAdminPath } from "../../config/routes";

function areSetsEqual(left, right) {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
}

function EditIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function permissionCount(group) {
  const perms = group.permissions;
  if (Array.isArray(perms)) return perms.length;
  return group.permissions_count ?? "—";
}

function AddPermissionGroupModal({ customerSlug, appSlug, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isDirty = Boolean(form.name.trim() || form.description.trim());

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!customerSlug) return;
      setSaving(true);
      setError(null);
      try {
        if (appSlug) {
          await createUserManagementPermissionGroupForApp(customerSlug, appSlug, {
            name: form.name || null,
            description: form.description || null,
          });
        } else {
          await createUserManagementPermissionGroup(customerSlug, {
            name: form.name || null,
            description: form.description || null,
          });
        }
        onSaved({ type: "success", message: "Permission group created." });
        onClose();
      } catch (err) {
        setError(err.message || "Failed to create permission group");
        onSaved({ type: "error", message: err.message || "Failed to create permission group" });
      } finally {
        setSaving(false);
      }
    },
    [customerSlug, appSlug, form, onSaved, onClose]
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-pg-title"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200">
          <h2 id="add-pg-title" className="text-lg font-semibold text-slate-800">
            Add Permission Group
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 animate-notice-slide-in" role="alert">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="add-pg-name" className="block text-sm font-medium text-slate-700 mb-1">
              Name
            </label>
            <input
              id="add-pg-name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label htmlFor="add-pg-description" className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              id="add-pg-description"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Cancel
            </button>
            <CtaButton type="submit" disabled={saving || !isDirty}>
              {saving ? "Creating…" : "Add Permission Group"}
            </CtaButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function filterPermissionsBySearch(permissions, search) {
  const q = (search || "").trim().toLowerCase();
  if (!q) return permissions;
  return permissions.filter(
    (p) =>
      (p.name || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)
  );
}

function EditPermissionGroupModal({ group, customerSlug, appSlug, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const [permissionIds, setPermissionIds] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [permissionInGroupSearch, setPermissionInGroupSearch] = useState("");
  const [addPermissionSearch, setAddPermissionSearch] = useState("");

  useEffect(() => {
    if (!group) return;
    setForm({ name: group.name ?? "", description: group.description ?? "" });
    setPermissionIds((group.permissions || []).map((p) => p.id));
    setPermissionInGroupSearch("");
    setAddPermissionSearch("");
  }, [group]);

  useEffect(() => {
    if (!customerSlug) return;
    const fetchPerms = appSlug
      ? () => fetchUserManagementPermissionsForApp(customerSlug, appSlug)
      : () => fetchUserManagementPermissions(customerSlug);
    fetchPerms()
      .then(setAllPermissions)
      .catch(() => setAllPermissions([]));
  }, [customerSlug, appSlug]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const removePermission = useCallback((id) => {
    setPermissionIds((prev) => prev.filter((pid) => pid !== id));
  }, []);

  const addPermission = useCallback((id) => {
    setPermissionIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!customerSlug || !group) return;
      setSaving(true);
      setError(null);
      try {
        const payload = {
          name: form.name || null,
          description: form.description || null,
          permission_ids: permissionIds,
        };
        if (appSlug) {
          await updateUserManagementPermissionGroupForApp(customerSlug, appSlug, group.id, payload);
        } else {
          await updateUserManagementPermissionGroup(customerSlug, group.id, payload);
        }
        onSaved({ type: "success", message: "Permission group updated." });
        onClose();
      } catch (err) {
        setError(err.message || "Failed to update permission group");
        onSaved({ type: "error", message: err.message || "Failed to update permission group" });
      } finally {
        setSaving(false);
      }
    },
    [customerSlug, appSlug, group, form, permissionIds, onSaved, onClose]
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [onClose]);

  if (!group) return null;

  const originalPermissionIds = new Set((group.permissions || []).map((p) => p.id));
  const currentPermissionIds = new Set(permissionIds);
  const isDirty =
    form.name !== (group.name ?? "") ||
    form.description !== (group.description ?? "") ||
    !areSetsEqual(originalPermissionIds, currentPermissionIds);

  const idsSet = new Set(permissionIds);
  const permissionsInGroup = allPermissions.filter((p) => idsSet.has(p.id));
  const permissionsNotInGroup = allPermissions.filter((p) => !idsSet.has(p.id));
  const filteredPermissionsInGroup = filterPermissionsBySearch(permissionsInGroup, permissionInGroupSearch);
  const filteredPermissionsNotInGroup = filterPermissionsBySearch(permissionsNotInGroup, addPermissionSearch);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-pg-title"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200">
          <h2 id="edit-pg-title" className="text-lg font-semibold text-slate-800">
            Edit permission group
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 animate-notice-slide-in" role="alert">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="edit-pg-name" className="block text-sm font-medium text-slate-700 mb-1">
              Name
            </label>
            <input
              id="edit-pg-name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <label htmlFor="edit-pg-description" className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              id="edit-pg-description"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-slate-700 mb-2">Permissions in this group</span>
            <p className="text-xs text-slate-500 mb-2">Remove a permission by clicking the × next to it.</p>
            {permissionsInGroup.length > 0 && (
              <input
                type="search"
                value={permissionInGroupSearch}
                onChange={(e) => setPermissionInGroupSearch(e.target.value)}
                placeholder="Search by name or description…"
                className="w-full px-3 py-2 mb-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                aria-label="Search permissions in this group"
              />
            )}
            {permissionsInGroup.length === 0 ? (
              <p className="text-sm text-slate-500">No permissions in this group.</p>
            ) : filteredPermissionsInGroup.length === 0 ? (
              <p className="text-sm text-slate-500">No permissions match the search.</p>
            ) : (
              <ul className="space-y-1.5 mb-4 max-h-32 overflow-y-auto">
                {filteredPermissionsInGroup.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-slate-50">
                    <span className="text-sm text-slate-700">{p.name}</span>
                    <button
                      type="button"
                      onClick={() => removePermission(p.id)}
                      className="p-1 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-slate-500 shrink-0"
                      title="Remove permission"
                      aria-label={`Remove ${p.name}`}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <span className="block text-sm font-medium text-slate-700 mb-1">Add permission</span>
            {permissionsNotInGroup.length > 0 && (
              <input
                type="search"
                value={addPermissionSearch}
                onChange={(e) => setAddPermissionSearch(e.target.value)}
                placeholder="Search by name or description…"
                className="w-full px-3 py-2 mb-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                aria-label="Search permissions to add"
              />
            )}
            {permissionsNotInGroup.length === 0 ? (
              <p className="text-sm text-slate-500">All permissions are already in this group.</p>
            ) : filteredPermissionsNotInGroup.length === 0 ? (
              <p className="text-sm text-slate-500">No permissions match the search.</p>
            ) : (
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {filteredPermissionsNotInGroup.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-slate-50">
                    <span className="text-sm text-slate-700">{p.name}</span>
                    <button
                      type="button"
                      onClick={() => addPermission(p.id)}
                      className="text-xs font-medium text-slate-600 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 rounded px-2 py-1 border border-slate-300 hover:bg-slate-100"
                    >
                      Add
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Cancel
            </button>
            <CtaButton type="submit" disabled={saving || !isDirty}>
              {saving ? "Saving…" : "Save"}
            </CtaButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminPermissionGroupsPage({
  customerSlug,
  appSlug = null,
  selectedApp: _selectedApp = null,
  apps = [],
  pathname = "",
  onNavigate,
}) {
  const { fetchParams, query, setQuery, page, setPage, perPage, setPerPage, sort, order, setSort } = useListPageUrlState();
  const [result, setResult] = useState({ data: [], meta: { total_count: 0, total_pages: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  const openEdit = useCallback(
    async (group) => {
      setEditGroup(group);
      setEditModalLoading(true);
      try {
        const full = appSlug
          ? await fetchUserManagementPermissionGroupForApp(customerSlug, appSlug, group.id)
          : await fetchUserManagementPermissionGroup(customerSlug, group.id);
        setEditGroup(full);
      } catch (err) {
        setError(err.message || "Failed to load permission group");
        setEditGroup(null);
      } finally {
        setEditModalLoading(false);
      }
    },
    [customerSlug, appSlug]
  );

  const loadList = useCallback(() => {
    if (!customerSlug || !appSlug) return;
    setLoading(true);
    setError(null);
    fetchUserManagementPermissionGroupsForApp(customerSlug, appSlug, fetchParams)
      .then(setResult)
      .catch((err) => setError(err.message || "Failed to load permission groups"))
      .finally(() => setLoading(false));
  }, [customerSlug, appSlug, fetchParams]);

  useEffect(() => {
    if (!customerSlug || !appSlug) return;
    loadList();
  }, [customerSlug, appSlug, loadList]);

  const totalCount = result.meta?.total_count ?? result.data.length;
  const totalPages = result.meta?.total_pages ?? Math.max(1, Math.ceil(totalCount / perPage));
  const pageData = result.meta ? result.data : result.data.slice((page - 1) * perPage, page * perPage);

  const appOptions = (apps || []).map((a) => ({
    value: a.slug ?? a.appSlug ?? a.id,
    label: a.displayName || a.name || a.slug || a.id || "—",
  }));
  const handleAppChange = (slug) => {
    if (onNavigate && slug) onNavigate(buildAdminPath(pathname, "permission-groups", slug));
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <h2 className="text-xl font-semibold text-slate-800 shrink-0">Permission Groups</h2>
        <div className="w-full sm:w-64">
          <Select
            id="permission-groups-app-select"
            value={appSlug ?? undefined}
            onChange={handleAppChange}
            options={appOptions}
            placeholder="Select App"
          />
        </div>
      </div>
      {!appSlug && (
        <p className="text-slate-600 text-sm">Select an app to view and manage permission groups.</p>
      )}
      {appSlug && (
      <ListPageLayout
        title=""
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search by name or description…"
        actions={<CtaButton onClick={() => setShowAddGroup(true)}>Add Permission Group</CtaButton>}
        notice={notice}
        onNoticeDismiss={() => setNotice(null)}
        loading={loading}
        error={error}
        totalCount={totalCount}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
      >
      <table className="min-w-full divide-y divide-slate-200" role="grid">
        <thead className="bg-slate-100 border-b border-slate-200">
          <tr>
            <SortableTh
              label="Name"
              sortKey="name"
              currentSort={sort}
              currentOrder={order}
              onSort={setSort}
              className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
            />
            <SortableTh
              label="Description"
              sortKey="description"
              currentSort={sort}
              currentOrder={order}
              onSort={setSort}
              className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
            />
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
              # Permissions
            </th>
            <SortableTh
              label="Updated at"
              sortKey="updated_at"
              currentSort={sort}
              currentOrder={order}
              onSort={setSort}
              className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
            />
            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {pageData.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                No permission groups found.
              </td>
            </tr>
          ) : (
            pageData.map((group) => (
            <tr key={group.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3 text-sm text-slate-900">{group.name ?? "—"}</td>
              <td className="px-4 py-3 text-sm text-slate-700">{group.description ?? "—"}</td>
              <td className="px-4 py-3 text-sm text-slate-700">{permissionCount(group)}</td>
              <td className="px-4 py-3 text-sm text-slate-700">{formatDate(group.updated_at)}</td>
              <td className="px-4 py-3 text-right">
                <button
                  type="button"
                  onClick={() => openEdit(group)}
                  className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                  title="Edit"
                  aria-label="Edit permission group"
                >
                  <EditIcon className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))
          )}
        </tbody>
      </table>
    </ListPageLayout>
      )}
      {showAddGroup && (
        <AddPermissionGroupModal
          customerSlug={customerSlug}
          appSlug={appSlug}
          onClose={() => setShowAddGroup(false)}
          onSaved={(result) => {
            if (result?.message) setNotice({ type: result.type || "success", message: result.message });
            loadList();
          }}
        />
      )}
      {editGroup &&
        (editModalLoading ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" aria-busy="true">
            <div className="bg-white rounded-xl shadow-xl px-8 py-6 text-slate-600">Loading…</div>
          </div>
        ) : (
          <EditPermissionGroupModal
            group={editGroup}
            customerSlug={customerSlug}
            appSlug={appSlug}
            onClose={() => setEditGroup(null)}
            onSaved={(result) => {
              if (result?.message) setNotice({ type: result.type || "success", message: result.message });
              loadList();
            }}
          />
        ))}
    </>
  );
}
